/**
 * Error Handling Constants
 * Centralized configuration for error management and health checking
 */

/**
 * Health check configuration
 */
export const HEALTH_CHECK_CONFIG = {
  /** Default interval between health checks (milliseconds) */
  DEFAULT_INTERVAL_MS: 30000, // 30 seconds

  /** Minimum time between health checks to prevent throttling (milliseconds) */
  THROTTLE_MS: 5000, // 5 seconds

  /** Timeout for health check requests (milliseconds) */
  TIMEOUT_MS: 5000, // 5 seconds

  /** Number of consecutive failures before marking as critical */
  CRITICAL_THRESHOLD: 3,
} as const;

/**
 * Error retry configuration
 */
export const ERROR_RETRY_CONFIG = {
  /** Maximum number of retry attempts for retryable errors */
  MAX_RETRY_ATTEMPTS: 3,

  /** Base delay between retries (milliseconds) */
  BASE_RETRY_DELAY_MS: 1000, // 1 second

  /** Maximum delay between retries (milliseconds) */
  MAX_RETRY_DELAY_MS: 10000, // 10 seconds

  /** Exponential backoff multiplier */
  BACKOFF_MULTIPLIER: 2,
} as const;

/**
 * Error display configuration
 */
export const ERROR_DISPLAY_CONFIG = {
  /** Maximum number of errors to keep in state */
  MAX_ERRORS: 5,

  /** Auto-dismiss timeout for non-critical errors (milliseconds) */
  AUTO_DISMISS_MS: 10000, // 10 seconds

  /** Error notification duration (milliseconds) */
  NOTIFICATION_DURATION_MS: 6000, // 6 seconds
} as const;

/**
 * HTTP status codes for error classification
 */
export const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION_ERROR: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

/**
 * Error type definitions
 */
export const ERROR_TYPES = {
  NETWORK: 'network',
  AUTH: 'auth',
  VALIDATION: 'validation',
  SERVER: 'server',
  UNKNOWN: 'unknown',
} as const;

export type ErrorType = typeof ERROR_TYPES[keyof typeof ERROR_TYPES];

/**
 * Backend status definitions
 */
export const BACKEND_STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  CHECKING: 'checking',
  UNKNOWN: 'unknown',
} as const;

export type BackendStatus = typeof BACKEND_STATUS[keyof typeof BACKEND_STATUS];

/**
 * Critical error types
 */
export const CRITICAL_ERROR_TYPES = {
  BACKEND_DOWN: 'backend_down',
  TOTAL_FAILURE: 'total_failure',
  NETWORK_FAILURE: 'network_failure',
} as const;

export type CriticalErrorType = typeof CRITICAL_ERROR_TYPES[keyof typeof CRITICAL_ERROR_TYPES] | null;
